import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import DataLoadingScreen from './DataLoadingScreen';

const LoadingContext = createContext(null);

// A request that never settles must not hold the app behind the loading screen
// forever, so every operation gets its own expiry.
const MAX_OPERATION_MS = 90_000;

export const LoadingProvider = ({ children }) => {
  const operationsRef = useRef(new Map());
  const watchdogsRef = useRef(new Map());
  const hideTimerRef = useRef(null);
  const [snapshot, setSnapshot] = useState({
    isLoading: false,
    loadingText: 'Caricamento...',
    progress: 0,
    showPercentage: true,
  });

  const publish = useCallback((partial = {}) => {
    const operations = [...operationsRef.current.values()];
    const isLoading = operations.length > 0;
    const withProgress = operations.filter((op) => op.showPercentage);
    const progress = withProgress.length
      ? Math.round(withProgress.reduce((sum, op) => sum + (op.progress || 0), 0) / withProgress.length)
      : (isLoading ? 0 : 100);
    const latest = operations[operations.length - 1];
    setSnapshot((prev) => ({
      isLoading,
      loadingText: latest?.text || prev.loadingText || 'Caricamento...',
      progress,
      // Any operation that reports progress wins, so a spinner-only lock
      // registered later cannot hide a tracked request's percentage.
      showPercentage: isLoading ? withProgress.length > 0 : prev.showPercentage,
      ...partial,
    }));
  }, []);

  const endOperationRef = useRef(null);

  const beginOperation = useCallback((id, text = 'Recupero dei dati in corso...', options = {}) => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    if (!watchdogsRef.current.has(id)) {
      watchdogsRef.current.set(id, setTimeout(() => {
        watchdogsRef.current.delete(id);
        endOperationRef.current?.(id);
      }, options.maxDurationMs || MAX_OPERATION_MS));
    }
    operationsRef.current.set(id, {
      text,
      progress: 0,
      showPercentage: options.showPercentage !== false,
    });
    publish();
    return id;
  }, [publish]);

  const updateOperation = useCallback((id, progress) => {
    const current = operationsRef.current.get(id);
    if (!current) return;
    operationsRef.current.set(id, { ...current, progress });
    publish();
  }, [publish]);

  const endOperation = useCallback((id) => {
    operationsRef.current.delete(id);
    const watchdog = watchdogsRef.current.get(id);
    if (watchdog) {
      clearTimeout(watchdog);
      watchdogsRef.current.delete(id);
    }
    if (operationsRef.current.size === 0) {
      setSnapshot((prev) => ({ ...prev, progress: 100 }));
      hideTimerRef.current = setTimeout(() => {
        if (operationsRef.current.size === 0) {
          setSnapshot({
            isLoading: false,
            loadingText: 'Caricamento...',
            progress: 0,
            showPercentage: true,
          });
        }
      }, 400);
      return;
    }
    publish();
  }, [publish]);

  endOperationRef.current = endOperation;

  React.useEffect(() => {
    const watchdogs = watchdogsRef.current;
    const handleStart = (e) => beginOperation(
      e.detail.filePath || `dati_${Date.now()}`,
      e.detail.text || 'Recupero dei dati in corso...',
      { showPercentage: e.detail.showPercentage !== false },
    );
    const handleProgress = (e) => {
      if (e.detail.filePath) updateOperation(e.detail.filePath, e.detail.percentage);
    };
    const handleEnd = (e) => {
      if (e.detail.filePath) endOperation(e.detail.filePath);
    };
    window.addEventListener('global-loading-start', handleStart);
    window.addEventListener('global-loading-progress', handleProgress);
    window.addEventListener('global-loading-end', handleEnd);
    return () => {
      window.removeEventListener('global-loading-start', handleStart);
      window.removeEventListener('global-loading-progress', handleProgress);
      window.removeEventListener('global-loading-end', handleEnd);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      watchdogs.forEach((timer) => clearTimeout(timer));
      watchdogs.clear();
    };
  }, [beginOperation, endOperation, updateOperation]);

  const manualIdRef = useRef('manual-lock');
  const showLoader = useCallback((text = 'Recupero dei dati in corso...', _expectedSteps = 1, options = { showPercentage: true }) => {
    beginOperation(manualIdRef.current, text, options);
  }, [beginOperation]);

  const hideLoader = useCallback(() => {
    endOperation(manualIdRef.current);
  }, [endOperation]);

  const updateProgress = useCallback((percentage) => {
    updateOperation(manualIdRef.current, percentage);
  }, [updateOperation]);

  const simulateProgress = useCallback(async (promise, text = 'Elaborazione...', options = { showPercentage: true }) => {
    showLoader(text, 1, options);
    let intervalProgress = 0;
    const interval = setInterval(() => {
      intervalProgress = Math.min(intervalProgress + 10, 90);
      updateProgress(intervalProgress);
    }, 200);
    try {
      const result = await promise;
      clearInterval(interval);
      updateProgress(100);
      hideLoader();
      return result;
    } catch (error) {
      clearInterval(interval);
      hideLoader();
      throw error;
    }
  }, [hideLoader, showLoader, updateProgress]);

  const value = useMemo(() => ({
    isLoading: snapshot.isLoading,
    showLoader,
    hideLoader,
    updateProgress,
    simulateProgress,
    beginOperation,
    endOperation,
    updateOperation,
  }), [beginOperation, endOperation, hideLoader, showLoader, simulateProgress, snapshot.isLoading, updateOperation, updateProgress]);

  return (
    <LoadingContext.Provider value={value}>
      {snapshot.isLoading && (
        <DataLoadingScreen
          visible={snapshot.isLoading}
          percentage={snapshot.showPercentage ? snapshot.progress : null}
          text={snapshot.loadingText}
        />
      )}
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => useContext(LoadingContext);
export default LoadingContext;
